// src/pages/JewelleryPlans/Plans/PendingPauseRequests/PauseRequestModal.jsx

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  XIcon,
  CalendarIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ClockIcon,
} from "@heroicons/react/outline";
import { format, addMonths } from "date-fns";
import {
  checkPauseEligibilityThunk,
  pausePlanThunk,
  clearPauseData,
} from "../../../../store/thunks/jewelleryPlan/planThunks";
import { PAUSE_REASONS } from "../../../../constants/jewellery.constant";
import Button from "../../../../components/common/Button";
import { notifySuccess, notifyError } from "../../../../utils/notification";

const PauseRequestModal = ({ isOpen, onClose, plan }) => {
  const dispatch = useDispatch();
  const { pauseEligibility, pauseLoading, pauseRequested, pauseError } =
    useSelector((state) => state.jewelleryPlan?.plans || {});

  const [formData, setFormData] = useState({
    reason: "",
    reasonDetails: "",
    startDate: format(new Date(), "yyyy-MM-dd"),
    duration: 1,
    documentsProvided: [],
  });

  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Check eligibility when modal opens
  useEffect(() => {
    if (isOpen && plan?._id) {
      dispatch(checkPauseEligibilityThunk(plan._id));
    }
  }, [isOpen, plan?._id, dispatch]);

  // Handle successful pause request
  useEffect(() => {
    if (pauseRequested) {
      notifySuccess("Pause request submitted successfully!");
      setTimeout(() => {
        onClose();
        dispatch(clearPauseData());
      }, 2000);
    }
  }, [pauseRequested, onClose, dispatch]);

  // Handle errors
  useEffect(() => {
    if (pauseError) {
      notifyError(pauseError);
    }
  }, [pauseError]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles([...uploadedFiles, ...files]);
    setFormData({
      ...formData,
      documentsProvided: [
        ...formData.documentsProvided,
        ...files.map((f) => f.name),
      ],
    });
  };

  const handleRemoveFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    setFormData({
      ...formData,
      documentsProvided: newFiles.map((f) => f.name),
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.reason) {
      newErrors.reason = "Please select a reason";
    }

    if (!formData.reasonDetails || formData.reasonDetails.trim().length < 10) {
      newErrors.reasonDetails =
        "Please provide details (minimum 10 characters)";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Please select a start date";
    }

    if (formData.duration < 1 || formData.duration > 3) {
      newErrors.duration = "Duration must be between 1 and 3 months";
    }

    // Check if documents required for certain reasons
    const reasonsRequiringDocs = [
      PAUSE_REASONS.MEDICAL_EMERGENCY,
      PAUSE_REASONS.JOB_LOSS,
      PAUSE_REASONS.NATURAL_DISASTER,
    ];

    if (
      reasonsRequiringDocs.includes(formData.reason) &&
      uploadedFiles.length === 0
    ) {
      newErrors.documentsProvided =
        "Supporting documents are required for this reason";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      notifyError("Please fix the errors in the form");
      return;
    }

    if (!pauseEligibility?.eligible) {
      notifyError("Plan is not eligible for pause");
      return;
    }

    const result = await dispatch(
      pausePlanThunk({
        planId: plan._id,
        pauseData: formData,
      })
    );

    if (pausePlanThunk.fulfilled.match(result)) {
      // Success handled by useEffect
    }
  };

  // Calculate impact
  const calculateImpact = () => {
    if (!plan || !formData.duration) return null;

    const currentMaturity = new Date(plan.maturesOn);
    const newMaturity = addMonths(currentMaturity, formData.duration);

    return {
      currentMaturity,
      newMaturity,
      extensionDays: formData.duration * 30, // Approximate
    };
  };

  const impact = calculateImpact();

  // Get reason label
  const getReasonLabel = (reason) => {
    const labels = {
      [PAUSE_REASONS.FINANCIAL_TEMPORARY]: "Temporary Financial Issue",
      [PAUSE_REASONS.MEDICAL_EMERGENCY]: "Medical Emergency",
      [PAUSE_REASONS.JOB_LOSS]: "Job Loss",
      [PAUSE_REASONS.TRAVEL]: "Travel",
      [PAUSE_REASONS.FAMILY_EMERGENCY]: "Family Emergency",
      [PAUSE_REASONS.MATERNITY]: "Maternity",
      [PAUSE_REASONS.EDUCATION]: "Education",
      [PAUSE_REASONS.BUSINESS_LOSS]: "Business Loss",
      [PAUSE_REASONS.NATURAL_DISASTER]: "Natural Disaster",
      [PAUSE_REASONS.OTHER]: "Other",
    };
    return labels[reason] || reason;
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Request Plan Pause
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Plan: {plan?.planNumber}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={pauseLoading}
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Eligibility Status */}
          {pauseEligibility && (
            <div
              className={`rounded-lg p-4 ${
                pauseEligibility.eligible
                  ? "bg-green-50 border border-green-200"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <div className="flex">
                <div className="flex-shrink-0">
                  {pauseEligibility.eligible ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                  ) : (
                    <ExclamationCircleIcon className="h-5 w-5 text-red-600" />
                  )}
                </div>
                <div className="ml-3">
                  <h4
                    className={`text-sm font-medium ${
                      pauseEligibility.eligible
                        ? "text-green-800"
                        : "text-red-800"
                    }`}
                  >
                    {pauseEligibility.eligible
                      ? "Plan is Eligible for Pause"
                      : "Plan is Not Eligible for Pause"}
                  </h4>
                  {!pauseEligibility.eligible && pauseEligibility.reason && (
                    <p className="mt-1 text-sm text-red-700">
                      {pauseEligibility.reason}
                    </p>
                  )}
                  {pauseEligibility.eligible && (
                    <div className="mt-2 text-sm text-green-700 space-y-1">
                      <p>
                        • Remaining pause quota:{" "}
                        <strong>
                          {pauseEligibility.remainingPauseMonths} months
                        </strong>
                      </p>
                      <p>
                        • Pause instances used:{" "}
                        <strong>{pauseEligibility.pauseInstancesUsed}</strong>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          {pauseEligibility?.eligible && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Pause <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.reason}
                  onChange={(e) => handleChange("reason", e.target.value)}
                  className={`block w-full border rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.reason ? "border-red-300" : "border-gray-300"
                  }`}
                >
                  <option value="">Select a reason</option>
                  {Object.values(PAUSE_REASONS).map((reason) => (
                    <option key={reason} value={reason}>
                      {getReasonLabel(reason)}
                    </option>
                  ))}
                </select>
                {errors.reason && (
                  <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
                )}
              </div>

              {/* Reason Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Detailed Explanation <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.reasonDetails}
                  onChange={(e) =>
                    handleChange("reasonDetails", e.target.value)
                  }
                  rows={4}
                  placeholder="Please provide detailed explanation for the pause request..."
                  className={`block w-full border rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.reasonDetails ? "border-red-300" : "border-gray-300"
                  }`}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.reasonDetails.length} / 500 characters (minimum 10)
                </p>
                {errors.reasonDetails && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.reasonDetails}
                  </p>
                )}
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pause Start Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleChange("startDate", e.target.value)}
                    min={format(new Date(), "yyyy-MM-dd")}
                    className={`block w-full pl-10 border rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.startDate ? "border-red-300" : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.startDate}
                  </p>
                )}
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pause Duration (months){" "}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.duration}
                  onChange={(e) =>
                    handleChange("duration", parseInt(e.target.value))
                  }
                  className={`block w-full border rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.duration ? "border-red-300" : "border-gray-300"
                  }`}
                >
                  {[1, 2, 3].map((month) => (
                    <option
                      key={month}
                      value={month}
                      disabled={
                        month > (pauseEligibility?.remainingPauseMonths || 0)
                      }
                    >
                      {month} month{month > 1 ? "s" : ""}
                      {month > (pauseEligibility?.remainingPauseMonths || 0) &&
                        " (Exceeds quota)"}
                    </option>
                  ))}
                </select>
                {errors.duration && (
                  <p className="mt-1 text-sm text-red-600">{errors.duration}</p>
                )}
              </div>

              {/* Document Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supporting Documents
                  {[
                    PAUSE_REASONS.MEDICAL_EMERGENCY,
                    PAUSE_REASONS.JOB_LOSS,
                    PAUSE_REASONS.NATURAL_DISASTER,
                  ].includes(formData.reason) && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400">
                  <div className="space-y-1 text-center">
                    <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                      >
                        <span>Upload files</span>
                        <input
                          id="file-upload"
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileUpload}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PDF, JPG, PNG up to 10MB each
                    </p>
                  </div>
                </div>
                {errors.documentsProvided && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.documentsProvided}
                  </p>
                )}

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      Uploaded Files:
                    </p>
                    {uploadedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gray-50 p-2 rounded-md"
                      >
                        <span className="text-sm text-gray-700 truncate">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Impact Preview */}
              {impact && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex">
                    <ClockIcon className="h-5 w-5 text-blue-600" />
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-900">
                        Impact on Plan
                      </h4>
                      <div className="mt-2 text-sm text-blue-800 space-y-1">
                        <p>
                          • Current maturity:{" "}
                          <strong>
                            {format(impact.currentMaturity, "dd MMM yyyy")}
                          </strong>
                        </p>
                        <p>
                          • New maturity after pause:{" "}
                          <strong>
                            {format(impact.newMaturity, "dd MMM yyyy")}
                          </strong>
                        </p>
                        <p>
                          • Maturity extended by:{" "}
                          <strong>{formData.duration} month(s)</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Approval Notice */}
              {formData.duration > 2 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                    <ExclamationCircleIcon className="h-5 w-5 text-yellow-600" />
                    <div className="ml-3">
                      <p className="text-sm text-yellow-800">
                        <strong>Requires Admin Approval:</strong> Pause requests
                        longer than 2 months require admin approval. You will be
                        notified once your request is reviewed.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {pauseError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">{pauseError}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <Button
                  variant="secondary"
                  onClick={onClose}
                  disabled={pauseLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={pauseLoading}
                  disabled={!pauseEligibility?.eligible || pauseLoading}
                >
                  {pauseLoading ? "Submitting..." : "Submit Pause Request"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PauseRequestModal;
